import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration, } from '../../../data/configuration.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface ConsoleAndLowerRailViewProps {
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface ConsoleAndLowerRailViewContextType {
	selectedConsoleItemId: string;
	handleConsoleItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function ConsoleAndLowerRailView(props: ConsoleAndLowerRailViewProps) {

	// initial values.
	const initialConsoleItemId = Configuration.centreConsoleOption;
	const initialConsoleItemName = () => getItemName(initialConsoleItemId);

	// states.
	const [selectedConsoleItemId, setSelectedConsoleItemId] = useState(initialConsoleItemId);
	const [selectedConsoleItemName, setSelectedConsoleItemName] = useState(initialConsoleItemName);

	// handlers.

	const handleConsoleItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedConsoleItemId(id);
		setSelectedConsoleItemName(getItemName(id));

		// update the configuration.
		Configuration.centreConsoleOption = id;
		Configuration.updateCosts();

		// select the centre console option on the car.
		props.interiorConfigurator.selectCentreConsoleOption(id);
	};
	
	// return the HTML.
	return (
		<ConsoleAndLowerRailViewContext.Provider value={{ selectedConsoleItemId, handleConsoleItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-console-and-lower-rail">COCKPIT WOOD</h2>
					</div>

					<SectionPanel
						subHeading={'COCKPIT WOOD'}
						modalContent={CloudData.getModalContent(selectedConsoleItemId, CloudData.centreConsoleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedConsoleItemName}
							sectionPrice={CloudData.getPrice(selectedConsoleItemId, CloudData.centreConsoleOptions, { ref: false })}
						/>
						<ConsoleItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</ConsoleAndLowerRailViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const ConsoleAndLowerRailViewContext = createContext<ConsoleAndLowerRailViewContextType | undefined>(undefined);

function ConsoleItems() {
	return (
		<ViewContentRows maxRowLength={11}>
			<ConsoleItem id={'CW012'} imageFilePath={'ash-olive'} />
			<ConsoleItem id={'CW013'} imageFilePath={'rosewood-santos'} />
			<ConsoleItem id={'CW014'} imageFilePath={'oak-european-smoked'} />
			<ConsoleItem id={'CW015'} imageFilePath={'oak-american-smoked'} />
			<ConsoleItem id={'CW016'} imageFilePath={'blackened-ash'} />
			<ConsoleItem id={'CW017'} imageFilePath={'bog-oak-medullary'} />
			<ConsoleItem id={'CW005'} imageFilePath={'walnut'} />
			<ConsoleItem id={'CW008'} imageFilePath={'birch-and-aluminium'} />
			<ConsoleItem id={'CW009'} imageFilePath={'walnut-and-aluminium'} />
			<ConsoleItem id={'CW010'} imageFilePath={'kinetic-diamond'} />
			<ConsoleItem id={'CW011'} imageFilePath={'spokes-in-motion'} />
		</ViewContentRows>
	);
}

function ConsoleItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ConsoleAndLowerRailViewContext);
	if (!context) {
		throw new Error('ConsoleAndLowerRailViewContext must be used within a ConsoleAndLowerRailView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/centre-console-and-rail/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedConsoleItemId}
			onItemClicked={context.handleConsoleItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.centreConsoleOptions);
}
